import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API } from '../App';

const BranchContext = createContext(null);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children, user }) => {
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get the list of branch IDs the user has access to
  const getUserAccessibleBranchIds = useCallback(() => {
    if (!user) return [];
    
    // Super admin can access all branches
    if (user.role === 'super_admin') return null; // null means all branches
    
    // Collect all branches user has access to
    const accessibleIds = new Set();
    
    // Primary branch
    if (user.branch_id) {
      accessibleIds.add(user.branch_id);
    }
    
    // Additional branches from branch_ids array
    if (Array.isArray(user.branch_ids)) {
      user.branch_ids.forEach(id => accessibleIds.add(id));
    }
    
    return Array.from(accessibleIds);
  }, [user]);
  
  // Check if user can switch branches (super_admin, has permission, or has multiple branches)
  const canSwitchBranches = useMemo(() => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.can_switch_branches) return true;
    
    // User can switch if they have access to multiple branches
    const accessibleIds = getUserAccessibleBranchIds();
    return accessibleIds && accessibleIds.length > 1;
  }, [user, getUserAccessibleBranchIds]);
  
  // Get branches that should be shown in the switcher dropdown
  const getAccessibleBranches = useCallback(() => {
    if (!user) return [];
    
    // Super admin sees all branches
    if (user.role === 'super_admin') return branches;
    
    // Users with explicit permission see all branches
    if (user.can_switch_branches) return branches;
    
    // Otherwise, filter to only accessible branches
    const accessibleIds = getUserAccessibleBranchIds();
    if (!accessibleIds || accessibleIds.length === 0) return [];
    
    return branches.filter(b => accessibleIds.includes(b.id));
  }, [user, branches, getUserAccessibleBranchIds]);
  
  // Get effective branch ID from localStorage or user's branch
  const getEffectiveBranchId = useCallback(() => {
    // Check if there's a viewAsBranch in localStorage
    const viewAs = localStorage.getItem('viewAsBranch');
    
    if (viewAs) {
      // For super_admin or users with switch permission, allow any branch
      if (user?.role === 'super_admin' || user?.can_switch_branches) {
        return viewAs;
      }
      
      // For multi-branch users, only allow switching to accessible branches
      const accessibleIds = getUserAccessibleBranchIds();
      if (accessibleIds && accessibleIds.includes(viewAs)) {
        return viewAs;
      }
    }
    
    // Default to user's primary branch
    return user?.branch_id || 'C30';
  }, [user, getUserAccessibleBranchIds]);

  // Load branches on mount
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await api.get(`${API}/branches`);
        setBranches(response.data.branches || []);
        
        const effectiveBranchId = getEffectiveBranchId();
        const branch = response.data.branches?.find(b => b.id === effectiveBranchId);
        
        if (branch) {
          setCurrentBranch(branch);
        } else if (response.data.branches?.length > 0) {
          const defaultBranch = response.data.branches.find(b => b.is_default) || response.data.branches[0];
          setCurrentBranch(defaultBranch);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setCurrentBranch({ id: 'C30', code: 'C30', name: 'Birmingham', display_name: 'C30 Birmingham' });
      } finally {
        setLoading(false);
      }
    };

    loadBranches();
  }, [getEffectiveBranchId]);

  // Switch branch - saves to localStorage and reloads page
  const setViewAs = useCallback((branchId) => {
    // Check if user can switch to this branch
    if (user?.role !== 'super_admin' && !user?.can_switch_branches) {
      // For multi-branch users, verify they have access
      const accessibleIds = getUserAccessibleBranchIds();
      if (!accessibleIds || !accessibleIds.includes(branchId)) {
        console.warn('User does not have access to branch:', branchId);
        return;
      }
    }
    
    console.log('Switching to branch:', branchId);
    localStorage.setItem('viewAsBranch', branchId);
    
    // Force full page reload to ensure all components refetch with new branch
    window.location.reload();
  }, [user, getUserAccessibleBranchIds]);

  // Clear view-as mode
  const clearViewAs = useCallback(() => {
    localStorage.removeItem('viewAsBranch');
    window.location.reload();
  }, []);

  // Check if in view-as mode (viewing a different branch than primary)
  const isViewingAs = useCallback(() => {
    const viewAs = localStorage.getItem('viewAsBranch');
    if (!viewAs) return false;
    
    // Check if viewAs is different from user's primary branch
    return viewAs !== user?.branch_id;
  }, [user?.branch_id]);
  
  // Get the branch object being viewed (if viewing as another branch)
  const viewingAsBranch = useMemo(() => {
    const viewAs = localStorage.getItem('viewAsBranch');
    if (!viewAs || viewAs === user?.branch_id) return null;
    return branches.find(b => b.id === viewAs) || null;
  }, [branches, user?.branch_id]);

  // Get effective branch object
  const getEffectiveBranch = useCallback(() => {
    const branchId = getEffectiveBranchId();
    return branches.find(b => b.id === branchId) || currentBranch;
  }, [getEffectiveBranchId, branches, currentBranch]);

  const value = {
    currentBranch,
    branches,
    loading,
    setViewAs,
    clearViewAs,
    getEffectiveBranch,
    getEffectiveBranchId,
    isViewingAs,
    viewingAsBranch,
    canSwitchBranches,
    getAccessibleBranches,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export default BranchContext;
